import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { HttpHeadersService } from '../../core/shared-service/http-headers.service';
import { MenuModel } from '../../post-login/menu/menu.model'

import {
  FacilitiesResponse,
  FacilityConsoleEquipmentsObject,
  FacilityObject,
  FacilitySummaryObject,
  PopUps,
  RFIDMemberResponse,
  UpdateRFIDMember,
} from './facility.model';
// import { AuthenticationService } from './authentication.service';

enum STATE {
  FRESH,
  LOADING,
  LOADED,
}

@Injectable({
  providedIn: 'root',
})
export class FacilityService {
  private _facilities = new FacilitiesResponse();
  private _facilitiesMap = new Map<String, FacilitySummaryObject>();
  private _popups: PopUps;
  private _manageFacilityHint: string;

  private _selectedFacility: FacilityObject = new FacilityObject();
  private _selectedFacilitySource = new Subject<FacilityObject>();
  public selectedFacilitySource = this._selectedFacilitySource.asObservable();
  private _selectedFacilityListSource = new Subject<string[]>();
  public selectedFacilityListSource = this._selectedFacilityListSource.asObservable();

  private _menus = new Subject<MenuModel|null>();
  public menus$ = this._menus.asObservable();

  private _refreshFacilities = new Subject();
  public refreshFacilities$ = this._refreshFacilities.asObservable();

  private _facilitiesConsoleMap = new Map<String, FacilityConsoleEquipmentsObject[]>();
  private _facilitiesBs =  new Subject<any>();
  public facilitiesBs$ = this._facilitiesBs.asObservable();

  // state
  private _state: STATE = STATE.FRESH;

  private _delinkFacilitySubscriber = new Subject<{facilityId:string,childFacilityId:string[]}>();
  public delinkFacilitySubscriber = this._delinkFacilitySubscriber.asObservable();
  constructor(private _http: HttpClient, private _header: HttpHeadersService) {} //, private _auth: AuthenticationService

  // counter is only for testing purpose..... need to delete after all
  get(): Promise<FacilitiesResponse> {
    return new Promise((resolve, reject) => {
      if (this._state === STATE.LOADED) {
        // loaded data
        resolve(this._facilities);
      } else if (this._state === STATE.LOADING) {
        this._facilitiesBs.subscribe(() => {
          resolve(this._facilities);
        });
      } else {
        this.fechFacilities();
        this._facilitiesBs.subscribe((res:any) => {
          if(res.errorMessage === 'error'){
            reject(res.status);
            this._state = STATE.FRESH;
          }else{
            resolve(this._facilities);
          }
          
        });
      }
    });
  }

  private fechFacilities() {
    this._state = STATE.LOADING;
    const headers = this._header.bearerHttpAuthorizationHeader();
    this._http.get(`${environment.servicePath}/lfasset_facility?t=${Date.now()}`, { headers }).subscribe(
      (res: FacilitiesResponse) => {
        this._facilities = JSON.parse(JSON.stringify(res));

        // manage popups
        this._popups = new PopUps();
        for (let _key in this._popups) {
          this._popups[_key] = res[_key] === 'true';
        }

        // default selected facility
        this.selectFacility(
          res.lastSelectedFacility || res.facilities[0].facilityId,
          res.lastSelectedFacility === null
        );
        
        this.updateSelectedFacilities([this._selectedFacility.facilityId]);

        // menu get
        this.menu();

        // need to select default facility and stream it throught rxjs
        this._facilitiesBs.next(res);
        this._state = STATE.LOADED;
      },
      (err: any) => {
        console.error('API failed.');
        this._facilitiesBs.next({errorMessage: 'error', status: err.status});
      }
    );
  }

  isEligibleLFODPremiumConsole(facilityData:any){
    if(facilityData.consoleEquipments){
      const lfodConsoles= ['superman','mulder','hal', 'everest', 'neo_se4', 'falco'];
      let consoleTypes = facilityData.consoleEquipments.map((val:any) => val.consoleType);
      let isEligibleConsoleType = consoleTypes.some((t:any) => lfodConsoles.includes(t));
      if(isEligibleConsoleType){
        return true;
      }else{
        return false;
      }
    }
    return false;
  }

  selectedFacilityId() {
    return this._selectedFacility?.facilityId;
  }

  selectedFacility() {
    return this._selectedFacility;
  }

  getManageFacilityHint():string{
    return this._manageFacilityHint;
  }

  setManageFacilityHint(hint : string){
      this._manageFacilityHint = hint;
  }

  selectedMultiFacilities() : string[]{
    return this._facilities.lastSelectedFacilityList;
  }

  unselectMultiFacilities(){
    this._facilities.lastSelectedFacilityList = [this._facilities.lastSelectedFacilityList[0]];
  }

  allFacilities(): Map<String, FacilitySummaryObject> {
    if (this._facilities) {
      this._facilities.facilities.map((fac) => {
        this._facilitiesMap.set(fac.facilityId, fac.summary);
      });
    }
    return this._facilitiesMap;
  }

  updateSelectedFacilities(data: string[]) {
    this._facilities.lastSelectedFacilityList = data;
  }

  getFacilityConsoleMap(): Map<String, FacilityConsoleEquipmentsObject[]>{
    if(this._facilities){
      this._facilities.facilities.map((fac) => {
        this._facilitiesConsoleMap.set(fac.facilityId, fac.consoleEquipments);
      });
    }
    return this._facilitiesConsoleMap;
  }

  getFacilitityConsole(facilityId: string) {
    return this.getFacilityById(facilityId)?.consoleEquipments;
  }

  getFacilityById(facilityId: string | undefined) {
    if(!facilityId) return undefined;
    return this._facilities?.facilities?.find(f => f.facilityId === facilityId);
  }

  isOneFacilityManager(): boolean {
    return this._facilities.facilities.length === 1;
  }

  selectFacility(id: string, needToUpdate: boolean) {
    this._menus.next(null);
    // check same
    const _selectedFacilityID = this._selectedFacility.facilityId;
    if (id !== _selectedFacilityID) {
      this._selectedFacility = <FacilityObject>this._facilities.facilities.find((facility) => {
        return facility.facilityId === id;
      });

      // check the selected facility available on facilities list.
      if (!this._selectedFacility || !this._selectedFacility['facilityId']) {
        this._selectedFacility = this._facilities.facilities[0];
      }
      // update facility preference for current user
      // do not save temp facility
      if (this._selectedFacility.summary?.facilityAddress && (needToUpdate || id !== this._selectedFacility.facilityId)) {
        this.updatedFacilityPreferenceForUser({ lastSelectedFacility: this._selectedFacility.facilityId });
      }

      // stream
      this._selectedFacilitySource.next(this._selectedFacility);
    }
  }

  getAnnouncements() {
    return this._popups;
  }

  deleteAcount() {
    const headers = this._header.bearerHttpAuthorizationHeader();
    return this._http.delete(`${environment.servicePath}/lfuser?isCCPA=false`, { headers });
  }

  setAnnouncementsState(key: string, value: boolean) {
    if (key) {
      this._popups[key] = value;
      this.updatedFacilityPreferenceForUser({ [key]: value.toString() });
    }
  }

  public updatedFacilityPreferenceForUser(data: any) {
    const headers = this._header.bearerHttpAuthorizationHeader();
    this._http.post(`${environment.servicePath}/lfasset_facility/user_preference`, data, { headers }).subscribe(
      () => {
        // no need to do anything with success
        this.menu();
      },
      () => {
        // need to set old facility id
        this.menu();
      }
    );
  }

  public updatedFacilityPreferenceForUser2(data: any) {
    const headers = this._header.bearerHttpAuthorizationHeader();
    return this._http.post(`${environment.servicePath}/lfasset_facility/user_preference`, data, { headers }).toPromise();
  }

  setFacilityUserPreference(key: string, value: boolean): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (key) {
        Object.assign(this._selectedFacility.summary, { key: value.toString() });
        let payload = { facilityId: this._selectedFacility.facilityId, [key]: value.toString() };
        this._saveFacilityUserPreference(payload).then(
          (res) => {
            resolve(res);
          },
          (err) => reject(err)
        );
      } else {
        reject(true);
      }
    });
  }

  //TODO: Need to remove this function and update reference when multi facility goes live
  private _saveFacilityUserPreference(data: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const headers = this._header.bearerHttpAuthorizationHeader();
      this._http
        .post(`${environment.servicePath}/lfasset_facility/facility_user_preference`, data, { headers })
        .subscribe(
          () => {
            // no need to do anything with success
            resolve(true);
          },
          () => {
            // need to set old facility id
            reject(true);
          }
        );
    });
  }

  reset() {
    this._state = STATE.FRESH;
    this._facilities = new FacilitiesResponse();
    this._popups = new PopUps();
    this._selectedFacility = new FacilityObject();
  }

  resetState(){
    this._state = STATE.FRESH;
  }

  add(data: any) {
    return new Promise((resolve, reject) => {
      const headers = this._header.bearerHttpAuthorizationHeader();
      this._http.post(`${environment.servicePath}/facility_maintenance/add_facility`, data, { headers }).subscribe(
        (res: { id: string }) => {
          // this._updatedFacilityPreferenceForUser({ lastSelectedFacility: res.id});
          this._state = STATE.FRESH;
          this.get().then((res2) => {
            this.selectFacility(res.id, true);
            this._refreshFacilities.next();
          });
          resolve(res);
        },
        (err) => resolve(err)
      );
    });
  }

  update(data: any) {
    return new Promise((resolve, reject) => {
      const headers = this._header.bearerHttpAuthorizationHeader();
      this._http.post(`${environment.servicePath}/facility_maintenance/update_facility`, data, { headers }).subscribe(
        (res) => {
          // update selected facility
          this._state = STATE.FRESH;
          this.get().then((res) => {
            this._selectedFacility = <FacilityObject>this._facilities.facilities.find((facility) => {
              return facility.facilityId === data.facilityId;
            });
            this.updateSelectedFacilities([this._selectedFacility.facilityId]);
            this.updatedFacilityPreferenceForUser({ lastSelectedFacility: this._selectedFacility.facilityId });
            this._selectedFacilitySource.next(this._selectedFacility);
            this._refreshFacilities.next();
            resolve(res);
          });
        },
        (err) => resolve(err)
      );
    });
  }

  updateOperatinHours(data: FacilitySummaryObject) {
    let fid = this.selectedFacilityId();
    return new Promise((resolve, reject) => {
      const headers = this._header.bearerHttpAuthorizationHeader();
      this._http.post(`${environment.servicePath}/facility_maintenance/update_facility`, data, { headers }).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => resolve(err)
      );
    });
  }

  remove(facilityId: string) {
    return new Promise((resolve, reject) => {
      const headers = this._header.bearerHttpAuthorizationHeader();
      this._http
        .delete(`${environment.servicePath}/facility_maintenance/delete_facility_manager?facilityId=${facilityId}`, {
          headers,
        })
        .subscribe(
          (res) => {
            this.deleteFacility(facilityId,[],'remove');
            resolve(res);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  updateDelinkFacility(obj:{facilityId:string,childFacilityId:string[]}){
    this._delinkFacilitySubscriber.next(obj);
  }

  deleteFacility(facilityId: string,childFacilityIds:string[],source:'remove'|'delink') {
    //If delink, removing parent and child facility as below.
    if(source === 'delink'){
      const index = this._facilities.facilities.findIndex((facility) => facility.facilityId === facilityId);
      this._facilities.facilities.splice(index, 1);
      if(childFacilityIds.length>0){
        childFacilityIds.forEach(val => {
          const index = this._facilities.facilities.findIndex((facility) => facility.facilityId === val);
          this._facilities.facilities.splice(index, 1);
        })
      }
    }else{
      const index = this._facilities.facilities.findIndex((facility) => facility.facilityId === facilityId);
      this._facilities.facilities.splice(index, 1);
    }
   
    const selectedFacilityId = this.selectedFacilityId();
    if(selectedFacilityId === facilityId) {
      this.selectFacility(this._facilities.facilities[0].facilityId, true);
    }    
    this._refreshFacilities.next();
  }

  menu() {
    const headers = this._header.bearerHttpAuthorizationHeader();
    // this.menus$ = this._http.get<MenuModel>(`${environment.servicePath}/lfasset_facility/menu`, { headers });
    this._http.get<MenuModel>(`${environment.servicePath}/lfasset_facility/menu`, { headers }).subscribe(
      res => {
        this._menus.next(res);
      }
    );
  }
}
